import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { addLibrary, removeLibrary } from '@lx/shared/utils';
import { fromEvent, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlywireV2ConfigurationVariables } from '../types/gateways/flywire-configuration-variables.type';
import { ENVIRONMENT } from '@lx/shared/tokens';
import { Environment } from '@lx/shared/types';
import { FlywireCheckout, FlywireCheckoutResult, FlywireSDKInstance } from './flywire-checkout-api';

/**
 * Flywire SDK loader function. This function is provided by Flywire and loads the Flywire SDK.
 * @param apiKey The API key provided by Flywire.
 * @return A promise that resolves to the Flywire SDK instance.
 */
declare function FlywireSDK(apiKey: string): Promise<FlywireSDKInstance>;

@Injectable()
export class FlywireGatewayV2Service implements OnDestroy {
  scriptV2LoadedSubject = new Subject<Event>();

  private v2ScriptElement: HTMLScriptElement | undefined;
  private flywireV2Config: FlywireV2ConfigurationVariables | undefined;

  private readonly destroyRef = inject(DestroyRef);
  private readonly env = inject(ENVIRONMENT);

  constructor() {
    this.flywireV2Config = this.getFlywireV2Config(this.env.environment);
  }

  bootstrapV2(): void {
    this.v2ScriptElement = addLibrary(this.flywireV2Config.path);
    fromEvent(this.v2ScriptElement, 'load', { once: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.scriptV2LoadedSubject);
  }

  /**
   * Preload the Flywire V2 payment form. This is useful to speed up the payment process as the form will be loaded in the background.
   * @param sessionId The Flywire session ID for the payment.
   * @param container Optional. If provided, the payment form will be rendered inside the container. If not, it will be rendered in a popup.
   * @return A promise that resolves when the payment form is preloaded.
   */
  async preloadPayment(sessionId: string, container?: string) {
    const sdk = await FlywireSDK(this.flywireV2Config.apiKey);
    const elements = await sdk.elements();
    return await elements.create('payment', this.getFlywireParams(sessionId, container));
  }

  /**
   * Make a payment using a preloaded Flywire V2 checkout instance. We use any for the promise as we don't have typing for it from Flywire.
   * @param checkout The preloaded Flywire checkout instance.
   * @param container Optional. If provided, the payment form will be rendered inside the container. If not, it will be rendered in a popup.
   * @return A promise that resolves when the payment is successful or rejects if there is an error.
   */
  async mount(checkout: FlywireCheckout, container?: string): Promise<FlywireCheckoutResult> {
    if (container) {
      checkout.mount(container);
    } else {
      checkout.mount();
    }

    return new Promise((resolve, reject) => {
      checkout.onEvent('success', resolve);
      checkout.onEvent('error', reject);
    });
  }

  /**
   * Make a payment using Flywire V2 SDK. We use any for the promise as we don't have typing for it from Flywire.
   * @param sessionId The Flywire session ID for the payment.
   * @return A promise that resolves when the payment is successful or rejects if there is an error.
   */
  async makePaymentV2(sessionId: string): Promise<FlywireCheckoutResult> {
    const sdk = await FlywireSDK(this.flywireV2Config.apiKey);
    const elements = await sdk.elements();
    const checkout = await elements.create('payment', this.getFlywireParams(sessionId));
    return await this.mount(checkout);
  }

  ngOnDestroy(): void {
    if (this.v2ScriptElement) {
      removeLibrary(this.v2ScriptElement);
    }
  }

  private getFlywireParams(sessionId: string, container?: string) {
    if (container) {
      return { sessionId, displayMode: 'container' };
    } else {
      return { sessionId };
    }
  }

  private getFlywireV2Config(env: Environment): FlywireV2ConfigurationVariables {
    if (env === 'production') {
      return {
        path: 'https://artifacts.flywire.com/sdk/js/v0/main.js',
        apiKey: 'fk_aWY0dVpXNExLUEUySzgzUmw2MkVUQT09',
      };
    } else {
      return {
        path: 'https://artifacts.flywire.com/sdk/js/v0/sandbox.main.js',
        apiKey: 'fk_RXI3YVhKcmhkNStqTW02cDFFay9XUT09',
      };
    }
  }
}
